import { Controller } from '@hotwired/stimulus';

const AI_PATH_MARKERS = ['/analyse', 'reanalyse', '/workouts/auto-plan/', '/workouts/add', '/routines/add', '/feeling-insights/analyze', '/advanced-insights/analyze-feelings', '/add-food-item'];

export class AiLoadingController extends Controller<HTMLElement> {
    static targets = ['overlay', 'title', 'message'];
    declare readonly overlayTarget: HTMLElement;
    declare readonly titleTarget: HTMLElement;
    declare readonly messageTarget: HTMLElement;

    connect(): void {
        document.addEventListener('submit', this.handleSubmit, true);
        document.addEventListener('turbo:submit-end', this.handleTurboSubmitEnd);
        document.addEventListener('turbo:before-render', this.hide);
        document.addEventListener('turbo:frame-render', this.hide);
        window.addEventListener('pageshow', this.hide);
    }

    disconnect(): void {
        document.removeEventListener('submit', this.handleSubmit, true);
        document.removeEventListener('turbo:submit-end', this.handleTurboSubmitEnd);
        document.removeEventListener('turbo:before-render', this.hide);
        document.removeEventListener('turbo:frame-render', this.hide);
        window.removeEventListener('pageshow', this.hide);
    }

    private handleSubmit = (event: SubmitEvent): void => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !this.isAiForm(form)) return;
        if (form.dataset.aiLoadingCustom === 'true' || form.closest('[data-controller~="analysis-form"], [data-controller~="manual-entry"]')) return;
        const path = new URL(form.action || window.location.href, window.location.href).pathname;
        this.titleTarget.textContent = form.dataset.aiLoadingTitle || 'Ovasai is working';
        this.messageTarget.textContent = form.dataset.aiLoadingMessage || this.messageFor(path);
        this.overlayTarget.classList.remove('hidden');
        this.overlayTarget.classList.add('flex');
        document.body.classList.add('overflow-hidden');
        form.setAttribute('aria-busy', 'true');
    };

    private handleTurboSubmitEnd = (event: Event): void => {
        const form = event.target;
        const frameSubmission = form instanceof HTMLFormElement && form.closest('turbo-frame');
        if (frameSubmission || (event as CustomEvent<{ success?: boolean }>).detail?.success === false) {
            this.hide();
        }
    };

    private hide = (): void => {
        this.overlayTarget.classList.add('hidden');
        this.overlayTarget.classList.remove('flex');
        document.body.classList.remove('overflow-hidden');
    };

    private isAiForm(form: HTMLFormElement): boolean {
        if (form.dataset.aiLoading === 'true') return true;
        const path = new URL(form.action || window.location.href, window.location.href).pathname.toLowerCase();
        if (path === '/workouts/auto-plan/confirm') return false;
        return AI_PATH_MARKERS.some(marker => path.includes(marker));
    }

    private messageFor(path: string): string {
        if (path.includes('auto-plan/questions')) return 'Preparing a few useful questions for your plan.';
        if (path.includes('auto-plan/generate')) return 'Building your workouts, targets and recommended schedule.';
        if (path.includes('/workouts/add')) return 'Saving your workout and preparing its calorie estimate.';
        if (path.includes('feeling')) return 'Reviewing your entries and preparing your insights.';
        if (path.includes('reanalyse') || path.includes('add-food-item')) return 'Reviewing the food details and calculating nutrition.';
        return 'Analysing the information you provided. This may take a moment.';
    }
}
